import { ref, watch } from 'vue'
import { createChangelog, listChangelogs, updateChangelog } from 'src/modules/changelogs/api.js'
import { Pagination } from 'src/modules/core/helpers/pagination.js'
import logger from 'src/services/logger/index.js'
import { useNotify } from 'src/modules/core/composables/useNotify.js'

const { successNotify, errorNotify } = useNotify()

/**
 * @type {{
 *   label: !string,
 *   value: !ChangelogStatus
 * }[]}
 */
export const STATUSES = [
  { label: 'Publié', value: 'PUBLISHED' },
  { label: 'Brouillon', value: 'DRAFT' }
]

const changelogs = ref([])

export default function useChangelogs(pageSize = Pagination.rowsPerPage) {
  const searchTerm = ref('')
  const status = ref('')
  const lastUnreadId = ref(null)
  const loading = ref(false)
  const submitting = ref(false)

  async function loadChangelogs(index, done) {
    if(index === 1) {
      changelogs.value = []
    }

    const pagination = {
      page: index,
      pageSize: pageSize
    }

    const filters = {
      searchQuery: searchTerm.value,
      status: status.value
    }

    try {
      loading.value = true
      const response = (await listChangelogs(pagination, filters)).data
      lastUnreadId.value = response.lastUnreadId
      changelogs.value = [...changelogs.value, ...response.data]
      const endReached = changelogs.value.length >= response.totalItems
      done(endReached)
    } catch (e) {
      errorNotify('Une erreur est survenue lors du chargement des notes de mise à jour')
      logger.error(e)
    } finally {
      loading.value = false
    }
  }

  /**
   * @param {Changelog} changelogData
   */
  async function addChangelog(changelogData) {
    try {
      submitting.value = true
      const changelog = (await createChangelog(changelogData)).data
      changelogs.value.unshift(changelog)
      successNotify('Note de mise à jour créée avec succès')
    } catch (e) {
      errorNotify('Une erreur est survenue lors de l\'ajout de la note de mise à jour')
      logger.error(e)
    } finally {
      submitting.value = false
    }
  }

  /**
   * @param changelogId
   * @param {Changelog} changelogData
   */
  async function modifyChangelog(changelogId, changelogData) {
    try {
      submitting.value = true
      const updatedChangelog = (await updateChangelog(changelogId, changelogData)).data
      const index = changelogs.value.findIndex(c => c.id === changelogId)
      if (index !== -1) {
        changelogs.value[index] = { ...changelogs.value[index], ...updatedChangelog }
      }
      successNotify('Note de mise à jour modifiée avec succès')
    } catch (e) {
      errorNotify('Une erreur est survenue lors de la modification de la note de mise à jour')
      logger.error(e)
    } finally {
      submitting.value = false
    }
  }

  watch([searchTerm, status], () => {
    changelogs.value = []
  })

  return {
    changelogs,
    searchTerm,
    status,
    loading,
    submitting,
    lastUnreadId,
    addChangelog,
    modifyChangelog,
    loadChangelogs
  }
}
